import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Link,
  IconButton,
} from '@mui/material';
import {
  Google,
  NoteAdd,
  Share,
  Devices,
  Speed,
  Lock,
  Groups,
  CheckCircle,
  Email,
  LocationOn,
  Checklist,
  MenuBook,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const features = [
  {
    icon: <NoteAdd sx={{ fontSize: 48 }} />,
    title: 'Sticky Notes & Shapes',
    description: 'Create colorful sticky notes, draw boxes, ellipses, diamonds, and connect them with smart connectors.',
  },
  {
    icon: <Share sx={{ fontSize: 48 }} />,
    title: 'Real-time Collaboration',
    description: 'Share your ThinkSpaces with team members. Control who can view or edit with granular permissions.',
  },
  {
    icon: <Devices sx={{ fontSize: 48 }} />,
    title: 'Works Everywhere',
    description: 'Access your ThinkSpaces from any device with a browser. No downloads or installations required.',
  },
  {
    icon: <Speed sx={{ fontSize: 48 }} />,
    title: 'Lightning Fast',
    description: 'Built for speed with instant updates and smooth interactions. No lag, no waiting.',
  },
  {
    icon: <Lock sx={{ fontSize: 48 }} />,
    title: 'Secure & Private',
    description: 'Your data is protected with enterprise-grade security. Only you and your team can access your work.',
  },
  {
    icon: <Groups sx={{ fontSize: 48 }} />,
    title: 'Built for Teams',
    description: 'Perfect for brainstorming, planning, and organizing ideas together with your team.',
  },
];

const benefits = [
  'Unlimited ThinkSpaces',
  'Unlimited ToDo lists',
  'Unlimited Notebooks',
  'Larger storage limits',
  'Priority saves',
  'Export (PDF / image / markdown)',
  'Share with team members',
];

const heroImages = [
  { src: '/images/hero-whiteboard.png', alt: 'Interactive whiteboard collaboration' },
  { src: '/images/hero-todolist.png', alt: 'ToDo list task management' },
  { src: '/images/hero-sharing.png', alt: 'Sharing and collaboration features' },
  { src: '/images/hero-nlp-input.png', alt: 'Natural language input' },
  { src: '/images/hero-nlp-full.png', alt: 'AI-powered organization' },
];

export const LandingPage = () => {
  const { loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  const goToLogin = () => navigate('/login');

  const nextHero = useCallback(() => {
    setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
  }, []);

  const prevHero = useCallback(() => {
    setCurrentHeroIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, []);

  // Auto-rotate hero images every 5 seconds
  useEffect(() => {
    const interval = setInterval(nextHero, 5000);
    return () => clearInterval(interval);
  }, [nextHero]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', overflowX: 'hidden' }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          py: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box
              component="img"
              src="/images/logo.png"
              alt="ThinkSpace"
              sx={{ height: { xs: 32, md: 40 } }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="text"
                onClick={goToLogin}
                sx={{ color: 'text.primary', display: { xs: 'none', sm: 'flex' } }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                onClick={goToLogin}
                sx={{
                  background: 'linear-gradient(135deg, #F34788 0%, #B049B6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #D13070 0%, #9A349D 100%)',
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          pt: { xs: 12, md: 8 },
          pb: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, #FDFCFD 0%, #FBF7FB 50%, #F5EDF5 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at 20% 20%, rgba(243, 71, 136, 0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(176, 73, 182, 0.08) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                label="Now in Beta"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(176, 73, 182, 0.1)',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' },
                  lineHeight: 1.1,
                  background: 'linear-gradient(135deg, #444E54 0%, #B049B6 50%, #F34788 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Visual Thinking,
                <br />
                Made Simple
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: 'text.secondary',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  maxWidth: 500,
                }}
              >
                Create collaborative whiteboards with sticky notes, shapes, and connectors.
                Share with your team and bring ideas to life together.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={goToLogin}
                  startIcon={<Google />}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #F34788 0%, #B049B6 100%)',
                    boxShadow: '0 8px 32px rgba(176, 73, 182, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #D13070 0%, #9A349D 100%)',
                      boxShadow: '0 12px 40px rgba(176, 73, 182, 0.45)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={goToLogin}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    borderWidth: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'primary.dark',
                      bgcolor: 'rgba(176, 73, 182, 0.04)',
                    },
                  }}
                >
                  Watch Demo
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} sx={{ color: 'text.secondary' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                  <Typography variant="body2">No credit card</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                  <Typography variant="body2">14-day trial</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '120%',
                    height: '120%',
                    background: 'radial-gradient(ellipse, rgba(176, 73, 182, 0.15) 0%, transparent 70%)',
                    borderRadius: '50%',
                    zIndex: 0,
                  },
                }}
              >
                {/* Hero Image Carousel */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    component="img"
                    src={heroImages[currentHeroIndex].src}
                    alt={heroImages[currentHeroIndex].alt}
                    sx={{
                      width: '100%',
                      borderRadius: 1,
                      boxShadow: '0 40px 80px rgba(68, 78, 84, 0.2), 0 20px 40px rgba(176, 73, 182, 0.15)',
                      transform: { md: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)' },
                      transition: 'all 0.5s ease',
                      '&:hover': {
                        transform: { md: 'perspective(1000px) rotateY(-2deg) rotateX(1deg)' },
                      },
                    }}
                  />
                  {/* Carousel Navigation */}
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <IconButton
                      onClick={prevHero}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'white' },
                      }}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    {heroImages.map((_, index) => (
                      <Box
                        key={index}
                        onClick={() => setCurrentHeroIndex(index)}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: index === currentHeroIndex ? 'primary.main' : 'rgba(176, 73, 182, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: index === currentHeroIndex ? 'primary.main' : 'rgba(176, 73, 182, 0.5)',
                          },
                        }}
                      />
                    ))}
                    <IconButton
                      onClick={nextHero}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'white' },
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, #F5EDF5 0%, #FFFFFF 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Features"
              sx={{
                mb: 2,
                bgcolor: 'rgba(243, 71, 136, 0.1)',
                color: 'secondary.main',
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h2"
              component="h2"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Everything You Need to Think Better
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Powerful tools designed for simplicity. Start creating in seconds.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 1,
                    background: 'linear-gradient(145deg, #FFFFFF 0%, #FBF7FB 100%)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 24px 48px rgba(176, 73, 182, 0.15)',
                      '& .feature-icon': {
                        transform: 'scale(1.1)',
                        color: 'secondary.main',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      className="feature-icon"
                      sx={{
                        color: 'primary.main',
                        mb: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Screenshot Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-50%',
            width: '200%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(243, 71, 136, 0.03) 0%, rgba(176, 73, 182, 0.05) 50%, rgba(154, 52, 157, 0.03) 100%)',
            transform: 'skewY(-3deg)',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src="/images/dashboard.png"
                alt="ThinkSpace dashboard"
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  boxShadow: '0 32px 64px rgba(68, 78, 84, 0.15)',
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                label="Dashboard"
                sx={{
                  mb: 2,
                  bgcolor: 'rgba(176, 73, 182, 0.1)',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                Organize All Your Ideas in One Place
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Create as many ThinkSpaces as you need. Each one is a blank canvas
                ready for your ideas. Perfect for brainstorming sessions, project planning,
                meeting notes, and more.
              </Typography>
              <Stack spacing={2}>
                {['Create unlimited workspaces', 'Quick access to recent spaces', 'Share with specific team members'].map((item, i) => (
                  <Stack key={i} direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: 'rgba(176, 73, 182, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Also Includes Section - ToDo Lists & Notebooks */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
            Also Included
          </Typography>
          <Grid container spacing={3}>
            {/* ToDo Lists */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: 'rgba(176, 73, 182, 0.03)',
                  border: '1px solid',
                  borderColor: 'rgba(176, 73, 182, 0.1)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: 'rgba(176, 73, 182, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Checklist sx={{ fontSize: 28, color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      ToDo Lists
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Track tasks with drag-and-drop ordering, subtasks, due dates, priority levels,
                      and recurrence. Share lists with your team.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            {/* Notebooks */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: 'rgba(243, 71, 136, 0.03)',
                  border: '1px solid',
                  borderColor: 'rgba(243, 71, 136, 0.1)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: 'rgba(243, 71, 136, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <MenuBook sx={{ fontSize: 28, color: 'secondary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Notebooks
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Take notes with keyboard or stylus. Choose from ruled, grid, dotted, or blank templates.
                      Export to PDF for printing or sharing.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7FB 100%)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="Pricing"
              sx={{
                mb: 2,
                bgcolor: 'rgba(251, 190, 77, 0.2)',
                color: '#996B00',
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h2"
              component="h2"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Simple, Affordable Pricing
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
            >
              Start free and upgrade when you need more.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Free Plan */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 4,
                  height: '100%',
                  background: '#FFFFFF',
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Free
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '3rem', md: '4rem' },
                        fontWeight: 800,
                        color: 'text.primary',
                      }}
                    >
                      $0
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontStyle: 'italic' }}>
                    Try it. Keep one of everything.
                  </Typography>

                  <Divider sx={{ my: 4 }} />

                  <Stack spacing={2} sx={{ mb: 5, textAlign: 'left' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>1 ThinkSpace whiteboard</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>1 ToDo list (unlimited items)</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>1 Notebook (unlimited pages)</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>Cloud-only (online required)</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>Basic save & load</Typography>
                    </Stack>
                  </Stack>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={goToLogin}
                    startIcon={<Google />}
                    sx={{
                      py: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      },
                    }}
                  >
                    Get Started Free
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    No credit card required
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Plan */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card
                elevation={0}
                sx={{
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 4,
                  overflow: 'visible',
                  position: 'relative',
                  height: '100%',
                  background: 'linear-gradient(145deg, #FFFFFF 0%, #FBF7FB 100%)',
                }}
              >
                <Chip
                  label="REMOVE LIMITS"
                  sx={{
                    position: 'absolute',
                    top: -14,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #F34788 0%, #B049B6 100%)',
                    color: 'white',
                    px: 2,
                  }}
                />
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Pro
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '3rem', md: '4rem' },
                        fontWeight: 800,
                        background: 'linear-gradient(135deg, #B049B6 0%, #F34788 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      $5.95
                    </Typography>
                    <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: '1.1rem' }}>
                      /mo, billed annually
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    $71.40 USD/year (NZ: +15% GST)
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontStyle: 'italic' }}>
                    Remove limits.
                  </Typography>

                  <Divider sx={{ my: 4 }} />

                  <Stack spacing={2} sx={{ mb: 5, textAlign: 'left' }}>
                    {benefits.map((benefit, i) => (
                      <Stack key={i} direction="row" spacing={2} alignItems="center">
                        <CheckCircle sx={{ color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{benefit}</Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={goToLogin}
                    startIcon={<Google />}
                    sx={{
                      py: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #F34788 0%, #B049B6 100%)',
                      boxShadow: '0 8px 32px rgba(176, 73, 182, 0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #D13070 0%, #9A349D 100%)',
                        boxShadow: '0 12px 40px rgba(176, 73, 182, 0.45)',
                      },
                    }}
                  >
                    Start Free Trial
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    No credit card required
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mt: 4 }}
          >
            Need a custom plan for your organization?{' '}
            <Link href="mailto:info@mythinkspace.app" color="primary" sx={{ fontWeight: 600 }}>
              Contact us
            </Link>
          </Typography>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #F34788 0%, #B049B6 55%, #9A349D 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.5,
          },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{ fontWeight: 700, mb: 3, color: 'white' }}
          >
            Ready to Think Better?
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, color: 'rgba(255,255,255,0.9)', maxWidth: 600, mx: 'auto' }}>
            Start using ThinkSpace today to collaborate and organize your ideas.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={goToLogin}
            startIcon={<Google />}
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.1rem',
              bgcolor: 'white',
              color: 'primary.main',
              fontWeight: 600,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: 'grey.100',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Started Free
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, bgcolor: '#444E54' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                component="img"
                src="/images/logo.png"
                alt="ThinkSpace"
                sx={{ height: 36, mb: 2, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
              />
              <Typography variant="body2" sx={{ color: 'grey.400', mb: 2, maxWidth: 280 }}>
                Visual thinking made simple. Create collaborative whiteboards for teams of all sizes.
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                A product by Advancer Limited
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Contact & Support
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Email sx={{ fontSize: 18, color: 'grey.500' }} />
                  <Link href="mailto:info@mythinkspace.app" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                    info@mythinkspace.app
                  </Link>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocationOn sx={{ fontSize: 18, color: 'grey.500', mt: 0.3 }} />
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    PO Box 25263<br />
                    Wellington 6140<br />
                    New Zealand
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Company
              </Typography>
              <Stack spacing={1}>
                <Link href="https://www.advancer.co.nz" target="_blank" rel="noopener noreferrer" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                  About Advancer
                </Link>
                <Link href="/privacy" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                  Privacy Policy
                </Link>
                <Link href="/terms" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                  Terms of Service
                </Link>
                <Link href="mailto:info@mythinkspace.app" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                  Contact
                </Link>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src="/images/icon.png"
                alt="ThinkSpace"
                sx={{ height: 24, opacity: 0.6 }}
              />
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                &copy; {new Date().getFullYear()}{' '}
                <Link
                  href="https://www.advancer.co.nz"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  Advancer Limited
                </Link>
                . All rights reserved.
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems="center">
              <Typography variant="caption" sx={{ color: 'grey.600' }}>
                ThinkSpace is operated by Advancer Limited (NZBN: 9429041814352)
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.600', display: { xs: 'none', sm: 'block' } }}>
                •
              </Typography>
              <Typography variant="caption" sx={{ color: 'grey.600' }}>
                Made in New Zealand
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
